import type {
  AggregatorLite,
  TemplateLite,
  SenderNameLite,
  StreamLite,
  MessageTypeLite,
  DomainLite,
} from '@botto/core';
import { ApiError, env, fetchWithAuth } from '@botto/core';
import { smsClient } from './client';
import type { Aggregator } from './types';

/**
 * Public lookup fetchers. Consumed by Tariffs (aggregators) and CDP
 * (aggregators/templates/senders/streams) via @botto/core/stores.
 */

function requireId(id: number | null | undefined): number {
  if (id == null) throw new Error('Lookup item without id');
  return id;
}

export async function fetchAggregators(): Promise<AggregatorLite[]> {
  const { data, error } = await smsClient.GET('/aggregators/', {
    params: { query: { offset: 0, limit: 1000 } },
  });
  if (error) throw error;
  return (data?.items ?? [])
    .filter((it) => it.id != null)
    .map((it) => ({
      id: requireId(it.id),
      smsc_id: (it as Aggregator).smsc_id ?? '',
      description: it.description ?? null,
      enabled: it.enabled ?? null,
    }));
}

export async function fetchTemplates(): Promise<TemplateLite[]> {
  const { data, error } = await smsClient.GET('/message_templates/', {
    params: { query: { offset: 0, limit: 1000 } },
  });
  if (error) throw error;
  return (data?.items ?? [])
    .filter((it) => it.id != null)
    .map((it) => ({
      id: requireId(it.id),
      name: it.name ?? null,
      template: it.template ?? null,
      aggregator_id: it.aggregator_id ?? null,
    }));
}

export async function fetchSenderNames(): Promise<SenderNameLite[]> {
  const { data, error } = await smsClient.GET('/sender_names/', {
    params: { query: { offset: 0, limit: 1000 } },
  });
  if (error) throw error;
  return (data?.items ?? [])
    .filter((it) => it.id != null)
    .map((it) => ({
      id: requireId(it.id),
      name: it.name ?? null,
      value: it.value ?? null,
      aggregator_id: it.aggregator_id ?? null,
    }));
}

export async function fetchStreams(): Promise<StreamLite[]> {
  const { data, error } = await smsClient.GET('/streams/', {
    params: { query: { offset: 0, limit: 1000 } },
  });
  if (error) throw error;
  return (data?.items ?? [])
    .filter((it) => it.id != null)
    .map((it) => ({
      id: requireId(it.id),
      name: it.name ?? null,
      system_id: it.system_id ?? null,
    }));
}

export async function fetchMessageTypes(): Promise<MessageTypeLite[]> {
  const { data, error } = await smsClient.GET('/message_type/', {
    params: { query: { offset: 0, limit: 1000 } },
  });
  if (error) throw error;
  return (data?.items ?? [])
    .filter((it) => it.id != null)
    .map((it) => ({
      id: requireId(it.id),
      title: it.title ?? null,
      sender_name: it.sender_name ?? null,
      type: it.type ?? null,
    }));
}

/**
 * Tracker domains via SMS proxy: GET /tracker/domains
 * (SMS API forwards to the tracking service).
 *
 * Uses raw fetchWithAuth: some proxies set Content-Length: 0 while still
 * streaming JSON, and openapi-fetch then yields `data: undefined` even though
 * DevTools shows a body — which emptied the Domains lookup/dropdown.
 */
export async function fetchDomains(): Promise<DomainLite[]> {
  const base = env.smsApiUrl.replace(/\/$/, '');
  const res = await fetchWithAuth(`${base}/tracker/domains`, { method: 'GET' }, 'sms');
  if (!res.ok) {
    let body: unknown = null;
    try {
      body = await res.json();
    } catch {
      body = await res.text();
    }
    throw new ApiError(`HTTP ${res.status} ${res.statusText}`, res.status, body);
  }

  const raw: unknown = await res.json();
  const rows = normalizeTrackerDomainRows(raw);
  return rows
    .filter((d) => d.id != null && Number.isFinite(Number(d.id)))
    .map((d) => {
      const id = Number(d.id);
      const label = (d.name ?? d.hostname ?? '').trim() || `#${id}`;
      return {
        domain_id: id,
        name: label,
        hostname: label,
      };
    });
}

function normalizeTrackerDomainRows(
  raw: unknown,
): Array<{ id?: number | string | null; name?: string | null; hostname?: string | null }> {
  if (Array.isArray(raw)) return raw;
  if (raw && typeof raw === 'object') {
    const obj = raw as Record<string, unknown>;
    for (const key of ['items', 'data', 'domains', 'results'] as const) {
      const v = obj[key];
      if (Array.isArray(v)) return v;
    }
  }
  return [];
}
